/**
 * Runtime configuration for the shared contact form.
 *
 * The form and its validation module make HTTP calls to the backend
 * (/api/landing/contact/, /api/landing/validate-email/, /api/auth/comuni/).
 *
 * Each consumer (frontend/ and landings/) lives on a different origin
 * and therefore needs a different baseURL:
 *
 *   - frontend/  (app.avmg.it, same-origin with backend)  → ''
 *   - landings/  (verdevercelli.it, cross-origin)         → 'https://avmg.it'
 *
 * Call `configureContactForm({ backendBaseUrl })` once at app startup
 * (typically in main.tsx). The value is a module-level singleton, read
 * lazily by the form and validation functions when they make requests.
 */

interface ContactFormConfig {
  /** Base URL for backend API calls. Empty string = same-origin. */
  backendBaseUrl: string
  /**
   * Optional credentials mode for fetch. Same-origin requests want
   * 'include' to send session cookies; cross-origin landings want
   * 'omit' because the backend endpoints don't need auth and cookies
   * wouldn't be sent anyway due to SameSite+cross-TLD.
   */
  credentials?: RequestCredentials
}

let config: ContactFormConfig = {
  backendBaseUrl: '',
  credentials: 'same-origin',
}

export function configureContactForm(next: Partial<ContactFormConfig>): void {
  config = { ...config, ...next }
}

export function getContactFormConfig(): Readonly<ContactFormConfig> {
  return config
}

/**
 * Build a full URL by joining the configured backendBaseUrl with a path.
 * Works for same-origin (empty baseURL → returns path as-is) and
 * cross-origin (prepends the absolute origin).
 */
export function apiUrl(path: string): string {
  const base = config.backendBaseUrl.replace(/\/$/, '')
  const p = path.startsWith('/') ? path : `/${path}`
  return base + p
}
